import { useState } from 'react';
import { Link, useRoute } from 'wouter';
import { ArrowLeft, ExternalLink, Play } from 'lucide-react';
import { useAdminOpsScopeDetail, useEnqueueAgentRun } from '../api/useAdminOps';
import DecisionCard from '../components/ops/DecisionCard';
import RunDetailDrawer from '../components/ops/RunDetailDrawer';
import StageRunButtons from '../components/ops/StageRunButtons';
import {
  HealthDot,
  MILESTONE_STATUS_STYLES,
  RUN_STATUS_STYLES,
  RefreshButton,
  StatusChip,
  TASK_LABELS,
  formatLocalDinner,
  tableSizeList,
  timeAgo,
  timeUntil,
} from '../components/ops/opsUi';

// Ops detail for ONE (event × neighborhood) scope — never combines cities or
// neighborhoods: stat bar → pipeline (runnable) → milestone rail → decisions
// → run history (drawer).

function StatCell({ label, value, alert }: { label: string; value: string | number; alert?: boolean }) {
  return (
    <div className="text-center px-3">
      <div className={`text-lg font-bold ${alert ? 'text-red-600' : ''}`}>{value}</div>
      <div className="text-[11px] text-dark-muted">{label}</div>
    </div>
  );
}

// The one manual task that isn't a pipeline stage.
function VenueResearchButton({
  eventTimeId,
  neighborhoodId,
}: {
  eventTimeId: string;
  neighborhoodId: string;
}) {
  const enqueue = useEnqueueAgentRun();
  return (
    <button
      onClick={() =>
        enqueue.mutate({ taskType: 'venue.research', eventTimeId, neighborhoodId })
      }
      disabled={enqueue.isPending}
      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800 disabled:opacity-50"
      title="Research candidate restaurants for this neighborhood (create_restaurant stays approval-gated)"
    >
      <Play className="w-4 h-4" /> Venue research
    </button>
  );
}

export default function AdminOpsEventDetail() {
  const [, params] = useRoute('/ops/:eventId/:neighborhoodId');
  const eventId = params?.eventId ? decodeURIComponent(params.eventId) : '';
  const neighborhoodId = params?.neighborhoodId
    ? decodeURIComponent(params.neighborhoodId)
    : '';
  const { data, isLoading, isFetching, refetch } = useAdminOpsScopeDetail(eventId, neighborhoodId);
  const [openRunId, setOpenRunId] = useState<string | null>(null);

  const detail = data?.data;
  const n = detail?.neighborhood;
  const pendingDecisions = detail?.decisions.filter((d) => d.status === 'pending') ?? [];
  const resolvedDecisions = detail?.decisions.filter((d) => d.status !== 'pending') ?? [];

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link href="/ops" className="text-dark-muted hover:text-primary-500">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="min-w-0">
              <h1 className="text-lg font-bold truncate">
                {n ? n.neighborhoodName : 'Event ops'}
                {detail && (
                  <span className="ml-2 text-sm font-normal text-dark-muted">
                    {detail.event.cityName} — {formatLocalDinner(detail.event)}
                  </span>
                )}
              </h1>
              {n && (
                <div className="flex items-center gap-2 text-xs text-dark-muted">
                  <HealthDot health={n.health} />
                  <span>{n.healthReason ?? 'on track'}</span>
                  {!n.automationEnabled && <span>· automation off</span>}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <RefreshButton onClick={() => refetch()} isFetching={isFetching} />
            <Link
              href={`/matching/${eventId}${
                n ? `?neighborhood=${encodeURIComponent(n.neighborhoodName)}` : ''
              }`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-600 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-800"
            >
              <ExternalLink className="w-4 h-4" /> Matching
            </Link>
            {n && <VenueResearchButton eventTimeId={eventId} neighborhoodId={neighborhoodId} />}
          </div>
        </div>

        {isLoading && <p className="text-sm text-dark-muted">Loading…</p>}
        {!isLoading && !detail && (
          <p className="text-sm text-dark-muted">No data for this event × neighborhood.</p>
        )}

        {detail && n && (
          <>
            {/* Stat bar */}
            <div className="flex flex-wrap items-center divide-x divide-neutral-200 dark:divide-neutral-700 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 py-3 mb-4">
              <StatCell label="booked" value={n.booked} />
              <StatCell label="seated" value={n.seated} />
              <StatCell label="unassigned" value={n.unassigned} alert={n.unassigned > 0} />
              <StatCell label="tables" value={tableSizeList(n.tableSizes)} />
              <StatCell label="locked" value={n.lockedTables} />
              {detail.event.eventType !== 'coffee' && (
                <StatCell
                  label="reserved"
                  value={`${n.reserved}/${n.reserved + n.reservationsNeeded}`}
                  alert={n.reservationsNeeded > 0}
                />
              )}
              <StatCell
                label="pending decisions"
                value={n.pendingDecisions}
                alert={n.pendingDecisions > 0}
              />
            </div>

            {/* Pipeline — runnable stages for this neighborhood */}
            <section className="mb-5 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                AI pipeline — click a stage to run it for {n.neighborhoodName}
              </h2>
              <StageRunButtons
                eventTimeId={eventId}
                neighborhoodId={neighborhoodId}
                milestones={n.milestones}
              />
            </section>

            {/* Milestone rail (incl. the system lock+notify step) */}
            <section className="mb-5">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                Timeline
              </h2>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {detail.milestones.map((m) => (
                  <button
                    key={m.task}
                    onClick={() => m.runId && setOpenRunId(m.runId)}
                    disabled={!m.runId}
                    className="min-w-[140px] rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-3 text-left disabled:cursor-default"
                  >
                    <StatusChip status={m.status} styles={MILESTONE_STATUS_STYLES} />
                    <p className="text-xs font-medium mt-1.5">{m.label}</p>
                    <p className="text-[11px] text-dark-muted mt-0.5">
                      {new Date(m.plannedAt) > new Date()
                        ? timeUntil(m.plannedAt)
                        : timeAgo(m.plannedAt)}
                    </p>
                  </button>
                ))}
              </div>
            </section>

            {/* Pending decisions */}
            {pendingDecisions.length > 0 && (
              <section className="mb-5">
                <h2 className="text-sm font-semibold text-amber-600 mb-2">
                  Needs a human ({pendingDecisions.length})
                </h2>
                <div className="space-y-2">
                  {pendingDecisions.map((d) => (
                    <DecisionCard key={d.id} decision={d} />
                  ))}
                </div>
              </section>
            )}

            {/* Run history */}
            <section className="mb-5">
              <h2 className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">
                Run history
              </h2>
              {detail.runs.length === 0 && (
                <p className="text-sm text-dark-muted">No runs yet for this neighborhood.</p>
              )}
              <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 divide-y divide-neutral-100 dark:divide-neutral-800 bg-white dark:bg-neutral-900">
                {detail.runs.map((run) => (
                  <button
                    key={run.id}
                    onClick={() => setOpenRunId(run.id)}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-neutral-50 dark:hover:bg-neutral-800"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <StatusChip status={run.status} styles={RUN_STATUS_STYLES} />
                      <span className="text-sm font-medium truncate">
                        {TASK_LABELS[run.taskType] ?? run.taskType}
                      </span>
                      <span className="text-xs text-dark-muted truncate">
                        {run.trigger}
                        {run.attempt > 1 && ` · attempt ${run.attempt}`}
                        {(run.metrics as { costUsd?: number } | null)?.costUsd != null &&
                          ` · $${((run.metrics as { costUsd: number }).costUsd).toFixed(2)}`}
                      </span>
                    </div>
                    <span className="text-xs text-dark-muted shrink-0">{timeAgo(run.createdAt)}</span>
                  </button>
                ))}
              </div>
            </section>

            {/* Resolved decisions (collapsed) */}
            {resolvedDecisions.length > 0 && (
              <details className="mb-5">
                <summary className="text-sm font-semibold text-dark-muted cursor-pointer">
                  Resolved decisions ({resolvedDecisions.length})
                </summary>
                <div className="space-y-2 mt-2">
                  {resolvedDecisions.map((d) => (
                    <DecisionCard key={d.id} decision={d} />
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </div>

      <RunDetailDrawer runId={openRunId} onClose={() => setOpenRunId(null)} />
    </>
  );
}
